import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "@/components/shared/StatusBadge";
import { Card } from "@/components/ui/card";
import type {
  TenantMember,
  TenantPendingInvite,
} from "@/services/tenantUsersService";
import { cn } from "@/lib/utils";

export type TenantUserMobileRow =
  | { type: "member"; data: TenantMember }
  | { type: "invite"; data: TenantPendingInvite };

type TenantUserMobileCardProps = {
  row: TenantUserMobileRow;
  onCardClick: () => void;
  menu: ReactNode;
  getMemberDisplayName: (m: TenantMember) => string;
};

function statusVariant(
  status: string
): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "disabled":
    case "expired":
      return "danger";
    default:
      return "info";
  }
}

export function TenantUserMobileCard({
  row,
  onCardClick,
  menu,
  getMemberDisplayName,
}: TenantUserMobileCardProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card
      className={cn(
        "overflow-hidden border-border p-4 text-left transition-colors duration-150",
        "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCardClick();
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          {row.type === "member" ? (
            <>
              <p className="font-medium text-foreground">
                {getMemberDisplayName(row.data)}
              </p>
              <p className="text-sm text-muted-foreground">{row.data.email}</p>
              <p className="text-sm text-foreground">
                {t(`users.tenant.role.${row.data.role}`)}
              </p>
              <StatusBadge variant={statusVariant(row.data.status)}>
                {t(`users.page.status.${row.data.status}`)}
              </StatusBadge>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">—</p>
              <p className="font-medium text-foreground">{row.data.email}</p>
              <p className="text-sm text-foreground">
                {t(`users.tenant.role.${row.data.role}`)}
              </p>
              <StatusBadge
                variant={row.data.expired ? "danger" : statusVariant("pending")}
              >
                {row.data.expired
                  ? t("users.page.status.expired")
                  : t("users.page.status.pending")}
              </StatusBadge>
            </>
          )}
        </div>
        <div
          className="shrink-0"
          data-interactive
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {menu}
        </div>
      </div>
    </Card>
  );
}
